import { CustomMessageType, GroupsInfo, MessageType } from 'types/chat.types';
import { formatRelative } from 'date-fns';
import enGB from 'date-fns/locale/en-GB';
import { dateFormats } from 'app/constants';

const formatRelativeLocale = {
  lastWeek: "'Last' eeee",
  yesterday: "'Yesterday'",
  today: "'Today'",
  tomorrow: "'Tomorrow'",
  nextWeek: "'Next' eeee",
  other: dateFormats.LIST_FORMAT,
};

const locale = {
  ...enGB,
  formatRelative: (token) => formatRelativeLocale[token],
};

interface ProcessMessagesParams {
  messages: MessageType[];
  lastReadTimestamp?: number;
  userGuid: string;
}

export const processMessages = (params: ProcessMessagesParams) => {
  const { lastReadTimestamp, messages, userGuid } = params;

  let newMessagesIndicatorAdded = false;
  const newMessages: MessageType[] = [];

  for (let i = 0; i < messages.length; i += 1) {
    const message = messages[i];

    const messageDateTimestamp =
      (message.created && message.created.timestamp) || undefined;

    const isMessageUnread =
      !lastReadTimestamp ||
      (messageDateTimestamp && lastReadTimestamp < messageDateTimestamp);

    if (!newMessagesIndicatorAdded) {
      // do not show new messages indicator for current user's messages
      const shouldAddNewMessagesIndicator =
        isMessageUnread && message.createdBy?.userGuid !== userGuid;

      if (shouldAddNewMessagesIndicator) {
        newMessagesIndicatorAdded = true;
        newMessages.push({
          customType: 'newMessagesIndicator',
          created: message.created,
        } as MessageType);
      }
    }

    newMessages.push(message);
  }

  return newMessages;
};

export function countPrependedMessages(
  messages: MessageType[],
  firstMessageGuid: string | null = null
) {
  let prependedItemCount = 0;
  const isFirstIndicator = messages[0]?.customType === CustomMessageType;

  if (firstMessageGuid === null) {
    return 0;
  }

  prependedItemCount = messages.findIndex((message) => {
    return message.guid === firstMessageGuid;
  });

  if (prependedItemCount < 1) {
    return 0;
  }

  return isFirstIndicator ? prependedItemCount - 1 : prependedItemCount;
}

export function mapDateGroupsInfo(messages: MessageType[]): GroupsInfo {
  const groupsInfo: GroupsInfo = {
    counts: [],
    titles: [],
    firstItemsIndexes: [],
  };

  messages.forEach((message) => {
    const messageDate = formatRelative(
      new Date(message.created.timestamp),
      new Date(),
      {
        locale,
      }
    );
    const groupIndex = groupsInfo.titles.findIndex(
      (title) => title === messageDate
    );

    // Add new group by date
    if (groupIndex === -1) {
      groupsInfo.counts.push(1);
      groupsInfo.titles.push(messageDate);
    } else {
      groupsInfo.counts[groupIndex] += 1;
    }
  });

  const indexesCounter = groupsInfo.counts.reduce<{
    firstItemsIndexes: number[];
    offset: number;
  }>(
    ({ firstItemsIndexes, offset }, count) => {
      return {
        firstItemsIndexes: [...firstItemsIndexes, offset],
        offset: offset + count,
      };
    },
    { firstItemsIndexes: [], offset: 0 }
  );

  groupsInfo.counts = [...groupsInfo.counts];
  groupsInfo.firstItemsIndexes = [...indexesCounter.firstItemsIndexes];

  return groupsInfo;
}
